import { useState } from "react";
import { Link } from "wouter";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import Navigation from "@/components/navigation";
import Footer from "@/components/footer";
import { useAuth } from "@/hooks/use-auth.tsx";
import { PROFESSIONAL_PRICE_LABEL } from "@shared/pricing";
import type { LucideIcon } from "lucide-react";
import { 
  Brain, 
  Code, 
  ChartLine, 
  Network, 
  BarChart3, 
  Play,
  Check,
  Lock,
  Trophy,
  Users,
  IdCard,
  University,
  CheckCircle,
  Clock,
  Star,
  Sparkles,
  Zap,
  Shield,
  Globe,
  TrendingUp,
  BookOpen,
  GraduationCap,
  Medal,
  Crown,
  Eye,
  Database,
  Bot,
  Cpu,
  Layers,
  Rocket,
  ExternalLink,
  FileText
} from "lucide-react";

type LearningTrack = {
  id: number;
  name: string;
  description: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  borderColor: string;
  gradient: string;
  btlLevel: string;
  totalCourses: number;
  completedCourses: number;
  progress: number;
  requiredTier: string;
  highlights: string[];
  comingSoon?: boolean;
  launchDate?: string;
};

const LEARNING_TRACKS: LearningTrack[] = [
  {
    id: 9,
    name: "Python: 100-Day Journey",
    description: "Master Python programming through a magical 100-day adventure! Perfect for beginners with engaging storylines, hands-on projects, and progressive skill building.",
    icon: GraduationCap,
    color: "text-orange-600",
    bgColor: "bg-orange-50 dark:bg-orange-950/20",
    borderColor: "border-orange-200 dark:border-orange-800",
    gradient: "from-orange-500 to-red-600",
    btlLevel: "Beginner",
    totalCourses: 5,
    completedCourses: 0,
    progress: 0,
    requiredTier: "guest",
    highlights: ["Python Fundamentals", "Game Development", "Data Analysis", "Web Creation", "Portfolio Projects"]
  },
  {
    id: 1,
    name: "AI Foundations & Discovery",
    description: "Master AI investigation techniques, pattern recognition, and digital evidence analysis through detective-themed learning.",
    icon: Brain,
    color: "text-blue-600",
    bgColor: "bg-blue-50 dark:bg-blue-950/20",
    borderColor: "border-blue-200 dark:border-blue-800",
    gradient: "from-blue-500 to-cyan-600",
    btlLevel: "BTL 1-2",
    totalCourses: 12,
    completedCourses: 8,
    progress: 67,
    requiredTier: "guest",
    highlights: ["Pattern Recognition", "Digital Evidence", "Basic AI Concepts", "Detective Methodology"]
  },
  {
    id: 2,
    name: "Programming & Modern AI Development",
    description: "Python mastery, vector databases, fine-tuning, RAG systems, agent frameworks, and cutting-edge AI development platforms.",
    icon: Code,
    color: "text-purple-600",
    bgColor: "bg-purple-50 dark:bg-purple-950/20",
    borderColor: "border-purple-200 dark:border-purple-800",
    gradient: "from-purple-500 to-violet-600",
    btlLevel: "BTL 3-6",
    totalCourses: 17,
    completedCourses: 12,
    progress: 57,
    requiredTier: "student",
    highlights: ["Python Programming", "Vector Databases", "RAG Systems", "AI Agents", "Modern IDEs"]
  },
  {
    id: 3,
    name: "Machine Learning Mastery",
    description: "Deep dive into ML algorithms, supervised/unsupervised learning, neural networks, and advanced model evaluation techniques.",
    icon: ChartLine,
    color: "text-green-600",
    bgColor: "bg-green-50 dark:bg-green-950/20",
    borderColor: "border-green-200 dark:border-green-800",
    gradient: "from-green-500 to-emerald-600",
    btlLevel: "BTL 4-5",
    totalCourses: 11,
    completedCourses: 6,
    progress: 33,
    requiredTier: "student",
    highlights: ["Supervised Learning", "Unsupervised Learning", "Model Evaluation", "Feature Engineering"]
  },
  {
    id: 4,
    name: "Deep Learning & Neural Networks",
    description: "Advanced neural architectures, CNNs, RNNs, transformers, attention mechanisms, and cutting-edge deep learning techniques.",
    icon: Network,
    color: "text-orange-600",
    bgColor: "bg-orange-50 dark:bg-orange-950/20",
    borderColor: "border-orange-200 dark:border-orange-800",
    gradient: "from-orange-500 to-amber-600",
    btlLevel: "BTL 5-6",
    totalCourses: 11,
    completedCourses: 0,
    progress: 0,
    requiredTier: "professional",
    highlights: ["Neural Networks", "CNNs", "RNNs", "Transformers", "Attention Mechanisms"]
  },
  {
    id: 8,
    name: "AI Fundamentals & Modern Techniques",
    description: "Modern AI foundations, prompt engineering, retrieval workflows, evaluation, responsible AI, and applied generative AI techniques.",
    icon: Cpu,
    color: "text-amber-600",
    bgColor: "bg-amber-50 dark:bg-amber-950/20",
    borderColor: "border-amber-200 dark:border-amber-800",
    gradient: "from-amber-500 to-yellow-600",
    btlLevel: "BTL 2-5",
    totalCourses: 16,
    completedCourses: 0,
    progress: 0,
    requiredTier: "professional",
    highlights: ["Prompt Engineering", "RAG", "LLMs", "Evaluation", "Responsible AI"]
  },
  {
    id: 10,
    name: "Micro-AI & Edge Intelligence",
    description: "Build ultra-lightweight AI models under 10KB for edge deployment. Master the revolutionary 10KB Docker/Kubernetes Command Assistant and deploy AI that works offline anywhere.",
    icon: Zap,
    color: "text-amber-600",
    bgColor: "bg-amber-50 dark:bg-amber-950/20",
    borderColor: "border-amber-200 dark:border-amber-800",
    gradient: "from-amber-500 to-orange-600",
    btlLevel: "BTL 6",
    totalCourses: 1,
    completedCourses: 0,
    progress: 0,
    requiredTier: "professional",
    highlights: ["10KB AI Models", "Edge Deployment", "DevOps Assistant", "Offline Intelligence", "Micro-Controllers"]
  },
  {
    id: 11,
    name: "System Design & Architecture",
    description: "Master large-scale distributed systems design. Learn to architect scalable infrastructure, design real-time systems, and solve complex engineering challenges faced by top tech companies.",
    icon: Layers,
    color: "text-slate-600",
    bgColor: "bg-slate-50 dark:bg-slate-950/20",
    borderColor: "border-slate-200 dark:border-slate-800",
    gradient: "from-slate-500 to-gray-600",
    btlLevel: "BTL 6",
    totalCourses: 5,
    completedCourses: 0,
    progress: 0,
    requiredTier: "professional",
    highlights: ["Distributed Systems", "Scalable Architecture", "Real-time Systems", "Storage Design", "System Optimization"]
  }
];

const PRICING_TIERS = [
  {
    name: "Free Trial Experience",
    price: "FREE",
    originalPrice: null,
    savings: "No Credit Card Required",
    description: "Complete Track 1 as extensive trial",
    features: [
      "Track 1: AI Foundations & Discovery (12 Courses)",
      "Interactive Detective Case Studies",
      "Community Forum Access",
      "Progress Tracking & Analytics",
      "Mobile Learning App Access",
      "Basic Certificate of Completion"
    ],
    cta: "Start Free Learning",
    popular: false,
    badge: "🆓 COMPLETELY FREE",
    highlight: "Perfect for beginners"
  },
  {
    name: "Complete AI Academy",
    price: PROFESSIONAL_PRICE_LABEL,
    originalPrice: null,
    savings: "One simple annual price",
    description: "One-time payment for 1-year access",
    features: [
      "All 8 Learning Tracks (75+ Courses in Active Tracks)",
      "Advanced Programming & AI Development",
      "Machine Learning & Deep Learning Complete",
      "Industry Applications & Real Projects",
      "Professional Code Playground",
      "All Professional Certificates",
      "1-Year Access - No Recurring Fees",
      "Priority Support & Updates"
    ],
    cta: "Get 1-Year Access",
    popular: true,
    badge: "🎯 BEST VALUE",
    highlight: "Most popular choice"
  }
];

const PLATFORM_STATS = [
  {
    number: "75+",
    label: "AI Courses",
    description: "Comprehensive curriculum",
    icon: BookOpen,
    color: "text-blue-600"
  },
  {
    number: "8",
    label: "Learning Tracks",
    description: "Structured pathways",
    icon: Globe,
    color: "text-green-600"
  },
  {
    number: "350+",
    label: "Detective Cases",
    description: "Real-world scenarios",
    icon: Eye,
    color: "text-purple-600"
  },
  {
    number: "100%",
    label: "Hands-On",
    description: "Practical learning",
    icon: Zap,
    color: "text-orange-600"
  }
];

export default function Home() {
  const { user } = useAuth();
  const [hoveredTrack, setHoveredTrack] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20" />
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-in slide-in-from-left duration-1000">
              <div className="flex items-center space-x-2 mb-6">
                <Badge variant="secondary" className="bg-green-500/20 text-green-100 border-green-400/30 animate-pulse">
                  <Sparkles className="w-3 h-3 mr-1" />
                  New: 8 Complete Tracks
                </Badge>
                <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-100 border-yellow-400/30">
                  <Crown className="w-3 h-3 mr-1" />
                  Professional Certificates
                </Badge>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                Master <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-pink-300 to-purple-300">Artificial Intelligence</span> & Machine Learning from{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-pink-300 to-purple-300">
                  Zero to Expert
                </span>
              </h1>
              
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Learn AI through story-based courses, practical cases, code practice, starter kits, and machine learning projects across 8 active learning tracks. Master deep learning, neural networks, Python programming, data science, generative AI, and modern AI development with professional certificates.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <Database className="w-4 h-4" />
                  </div>
                  <span>Vector Databases & RAG</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <Bot className="w-4 h-4" />
                  </div>
                  <span>Autonomous AI Agents</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <Layers className="w-4 h-4" />
                  </div>
                  <span>Modern Development Stack</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <Shield className="w-4 h-4" />
                  </div>
                  <span>Enterprise AI & Ethics</span>
                </div>
              </div>

              {/* Launch Pricing Alert */}
              <div className="bg-gradient-to-r from-red-600/90 to-pink-600/90 border border-red-400/50 rounded-xl p-4 mb-6 backdrop-blur-sm">
                <div className="flex items-center justify-center gap-2 text-white font-bold">
                  <Clock className="w-4 h-4 animate-pulse" />
                  {PROFESSIONAL_PRICE_LABEL} 1-Year Professional Access
                </div>
                <p className="text-center text-white/90 text-sm mt-1">
                  No recurring fees, no hidden tiers, and updates included during your active term
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="/payment-plans">
                  <Button size="lg" className="bg-white text-blue-700 hover:bg-gray-100 w-full sm:w-auto shadow-lg">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    See Pricing Plans
                  </Button>
                </Link>
                <Link href="/playground">
                  <Button size="lg" className="bg-white text-blue-700 hover:bg-gray-100 shadow-lg">
                    <Code className="w-4 h-4 mr-2" />
                    Try AI Code Editor
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center gap-6 mt-6 text-white/80">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>Free Track 1</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>AI Code Assistant</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>Starter Kit Apps</span>
                </div>
              </div>
            </div>

            <div className="relative animate-in slide-in-from-right duration-1000 delay-300">
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Brain className="w-5 h-5" />
                    Live AI Neural Network
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 items-center mb-6">
                    {/* Enhanced Neural network visualization */}
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="w-4 h-4 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-pulse shadow-lg" 
                             style={{ animationDelay: `${i * 0.2}s` }} />
                      ))}
                    </div>
                    <div className="space-y-2">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-pulse shadow-lg" 
                             style={{ animationDelay: `${i * 0.2 + 0.6}s` }} />
                      ))}
                    </div>
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="w-4 h-4 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full animate-pulse shadow-lg" 
                             style={{ animationDelay: `${i * 0.2 + 1.4}s` }} />
                      ))}
                    </div>
                    <div className="space-y-2">
                      {[1, 2].map((i) => (
                        <div key={i} className="w-4 h-4 bg-gradient-to-r from-red-400 to-rose-500 rounded-full animate-pulse shadow-lg" 
                             style={{ animationDelay: `${i * 0.2 + 2}s` }} />
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-900/90 rounded-lg p-4 font-mono text-sm backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-green-400">modern_ai_system.py</span>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                        <Zap className="w-3 h-3 mr-1" />
                        Training
                      </Badge>
                    </div>
                    <div className="space-y-1 text-gray-300">
                      <div><span className="text-yellow-400">import</span> <span className="text-green-400">torch</span> <span className="text-yellow-400">as</span> <span className="text-green-400">nn</span></div>
                      <div><span className="text-purple-400">model</span> = <span className="text-green-400">TransformerModel(</span></div>
                      <div className="ml-4"><span className="text-green-400">d_model=512, nhead=8</span></div>
                      <div><span className="text-green-400">)</span></div>
                      <div><span className="text-blue-400"># Vector embeddings ready</span></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Enhanced floating stats */}
              <div className="hidden sm:block absolute -bottom-4 left-4 bg-white rounded-xl p-4 shadow-xl border">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900">75+</p>
                    <p className="text-xs text-gray-600">Courses in Tracks</p>
                  </div>
                </div>
              </div>

              <div className="hidden sm:block absolute -top-4 right-4 bg-white rounded-xl p-4 shadow-xl border">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-red-500 rounded-lg flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900">8</p>
                    <p className="text-xs text-gray-600">Active Tracks</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Statistics */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Complete AI Academy Platform</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive AI education from foundations to enterprise leadership
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {PLATFORM_STATS.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center group">
                  <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${stat.color === 'text-blue-600' ? 'from-blue-500 to-cyan-600' : stat.color === 'text-green-600' ? 'from-green-500 to-emerald-600' : stat.color === 'text-purple-600' ? 'from-purple-500 to-violet-600' : 'from-orange-500 to-amber-600'} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold mb-2 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                    {stat.number}
                  </div>
                  <div className="font-semibold text-lg">{stat.label}</div>
                  <div className="text-sm text-muted-foreground">{stat.description}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Starter Kits Showcase */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Deploy Production-Ready AI Applications</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Professional starter kits with complete source code, instant deployment, and full customization
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <Card className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-500">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Bot className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <CardTitle className="text-lg">AI Chatbot Assistant</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground">
                  Intelligent conversational AI with natural language processing, context awareness, and multi-turn conversations
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="secondary" className="text-xs">OpenAI GPT</Badge>
                  <Badge variant="secondary" className="text-xs">React</Badge>
                  <Badge variant="secondary" className="text-xs">Node.js</Badge>
                </div>
                <Link href="/starter-kits">
                  <Button size="sm" className="w-full group-hover:bg-blue-600 transition-colors">
                    <Rocket className="w-4 h-4 mr-2" />
                    Deploy Now
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-purple-500">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <CardTitle className="text-lg">ML Analytics Dashboard</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground">
                  Real-time machine learning model monitoring with performance metrics, data visualization, and alerts
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="secondary" className="text-xs">TensorFlow</Badge>
                  <Badge variant="secondary" className="text-xs">Charts</Badge>
                  <Badge variant="secondary" className="text-xs">Python</Badge>
                </div>
                <Link href="/starter-kits">
                  <Button size="sm" className="w-full group-hover:bg-purple-600 transition-colors">
                    <Rocket className="w-4 h-4 mr-2" />
                    Deploy Now
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-green-500">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <Eye className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <CardTitle className="text-lg">Computer Vision AI</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground">
                  Advanced image recognition with object detection, classification, OCR, and real-time analysis
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="secondary" className="text-xs">OpenCV</Badge>
                  <Badge variant="secondary" className="text-xs">YOLO</Badge>
                  <Badge variant="secondary" className="text-xs">PyTorch</Badge>
                </div>
                <Link href="/starter-kits">
                  <Button size="sm" className="w-full group-hover:bg-green-600 transition-colors">
                    <Rocket className="w-4 h-4 mr-2" />
                    Deploy Now
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Link href="/starter-kits">
              <Button size="lg" variant="outline" className="group">
                <Rocket className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" />
                View All 8 Starter Kits
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Intelligent Code Editor Showcase */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">AI-Powered Code Editor & Playground</h2>
              <p className="text-xl text-muted-foreground mb-8">
                Professional development environment with smart AI assistance and real-time code analysis
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mt-1">
                    <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Smart AI Assistant</h3>
                    <p className="text-sm text-muted-foreground">Get the best answers automatically from multiple AI models working together</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mt-1">
                    <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Comment-Based Commands</h3>
                    <p className="text-sm text-muted-foreground">Use # commands for generate, explain, fix, optimize, debug, and refactor operations</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mt-1">
                    <Check className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Proactive Analysis</h3>
                    <p className="text-sm text-muted-foreground">Real-time code monitoring with automatic suggestions and error detection</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mt-1">
                    <Check className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Instant Responses</h3>
                    <p className="text-sm text-muted-foreground">Fast AI assistance with intelligent caching for common coding tasks</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="/playground">
                  <Button size="lg" className="w-full sm:w-auto">
                    <Code className="w-5 h-5 mr-2" />
                    Try Code Editor
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  <Play className="w-5 h-5 mr-2" />
                  Watch Demo
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gray-900 rounded-lg p-6 shadow-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="ml-4 text-gray-400 text-sm">AI Academy Pro Editor</span>
                </div>
                <div className="text-green-400 font-mono text-sm space-y-2">
                  <div><span className="text-blue-400"># generate</span><span className="text-gray-300"> user authentication function</span></div>
                  <div><span className="text-purple-400">def</span> <span className="text-yellow-400">authenticate_user</span><span className="text-white">(email, password):</span></div>
                  <div className="ml-4"><span className="text-gray-300"># AI generates complete function...</span></div>
                  <div className="ml-4"><span className="text-green-400">return</span> <span className="text-white">verify_credentials(email, password)</span></div>
                  <div className="mt-4"><span className="text-blue-400"># explain</span><span className="text-gray-300"> how this works</span></div>
                  <div className="text-gray-400 text-xs mt-2">💡 AI Assistant: This function validates user credentials...</div>
                </div>
                <div className="absolute -top-4 -right-4 bg-blue-900 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  AI Active
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Tracks Section */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">8 Active Story-Based Learning Tracks</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Move from AI foundations and Python into machine learning, deep learning, modern AI techniques, Micro-AI, and system design through guided cases and projects.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {LEARNING_TRACKS.map((track) => {
              const IconComponent = track.icon;
              const isLocked = track.requiredTier !== "guest" && !user;
              const isComingSoon = track.comingSoon;
              const isHovered = hoveredTrack === track.id;
              
              return (
                <Card 
                  key={track.id} 
                  className={`group relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] border-2 ${track.borderColor} ${track.bgColor} ${isLocked || isComingSoon ? 'opacity-75' : ''}`}
                  onMouseEnter={() => setHoveredTrack(track.id)}
                  onMouseLeave={() => setHoveredTrack(null)}
                >
                  {/* Track Number Badge */}
                  <div className="absolute top-4 right-4">
                    {isComingSoon ? (
                      <Badge className="bg-gradient-to-r from-orange-500 to-amber-600 text-white border-0 shadow-lg">
                        Coming Soon
                      </Badge>
                    ) : (
                      <Badge className={`bg-gradient-to-r ${track.gradient} text-white border-0 shadow-lg`}>
                        Track {track.id}
                      </Badge>
                    )}
                  </div>

                  {/* Coming Soon overlay */}
                  {isComingSoon && (
                    <div className="absolute inset-0 bg-background/85 backdrop-blur-[1px] z-10 flex items-center justify-center">
                      <div className="text-center">
                        <Clock className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                        <p className="text-sm font-medium text-foreground mb-1">Coming Soon</p>
                        <p className="text-xs text-muted-foreground">{track.launchDate}</p>
                      </div>
                    </div>
                  )}

                  {/* Lock overlay for premium tracks */}
                  {isLocked && !isComingSoon && (
                    <div className="absolute inset-0 bg-background/80 backdrop-blur-[1px] z-10 flex items-center justify-center">
                      <div className="text-center">
                        <Lock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm font-medium text-muted-foreground">Requires Subscription</p>
                      </div>
                    </div>
                  )}
                  
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-14 h-14 bg-gradient-to-br ${track.gradient} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <Badge variant="outline" className="border-gray-300 bg-white/90 text-gray-900 dark:border-gray-700 dark:bg-gray-950/80 dark:text-gray-100">
                        {track.btlLevel}
                      </Badge>
                    </div>
                    
                    <CardTitle className="text-xl font-bold leading-tight mb-2">
                      {track.name}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {track.description}
                    </p>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="space-y-4">
                      {/* Course Progress */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">Progress</span>
                        <span className="font-bold">{track.progress}%</span>
                      </div>
                      <Progress value={track.progress} className="h-3" aria-label={`${track.name} progress`} />
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-muted/50 rounded-lg p-3">
                          <div className="font-bold text-lg">{track.totalCourses}</div>
                          <div className="text-muted-foreground">Total Courses</div>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-3">
                          <div className="font-bold text-lg">{track.completedCourses}</div>
                          <div className="text-muted-foreground">Completed</div>
                        </div>
                      </div>

                      {/* Highlights */}
                      {isHovered && (
                        <div className="animate-in slide-in-from-bottom duration-300">
                          <div className="text-xs font-medium text-muted-foreground mb-2">Key Topics:</div>
                          <div className="flex flex-wrap gap-1">
                            {track.highlights.map((highlight, index) => (
                              <Badge key={index} variant="secondary" className="text-xs px-2 py-1">
                                {highlight}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {isComingSoon ? (
                        <Button 
                          className="w-full h-11 bg-gradient-to-r from-orange-500 to-amber-600 hover:opacity-90 text-white border-0 shadow-lg"
                          disabled
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Coming {track.launchDate}
                        </Button>
                      ) : (
                        <Link href={`/track/${track.id}`}>
                          <Button 
                            className={`w-full h-11 bg-gradient-to-r ${track.gradient} hover:opacity-90 text-white border-0 shadow-lg`}
                            disabled={isLocked}
                          >
                            {isLocked ? (
                              <>
                                <Lock className="w-4 h-4 mr-2" />
                                Requires Access
                              </>
                            ) : track.progress > 0 ? (
                              <>
                                <Play className="w-4 h-4 mr-2" />
                                Continue Learning
                              </>
                            ) : (
                              <>
                                <BookOpen className="w-4 h-4 mr-2" />
                                Start Track
                              </>
                            )}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>



      {/* Pricing Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Start free with Track 1, then unlock everything with one simple payment
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {PRICING_TIERS.map((tier, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl ${tier.popular ? 'border-2 border-blue-500 shadow-xl scale-105' : 'border hover:border-blue-200'}`}
              >
                {tier.popular && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-purple-500" />
                )}
                
                <CardHeader className="text-center pb-4">
                  <div className="mb-4">
                    <Badge className={tier.popular ? "bg-blue-700 text-white" : "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100"}>
                      {tier.badge}
                    </Badge>
                  </div>
                  
                  <CardTitle className="text-2xl font-bold mb-2">{tier.name}</CardTitle>
                  <div className="text-4xl font-bold mb-2">
                    {tier.price}
                    {tier.originalPrice && (
                      <span className="text-lg text-muted-foreground line-through ml-2">
                        {tier.originalPrice}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-green-700 dark:text-green-300 font-medium">{tier.savings}</p>
                  <p className="text-muted-foreground mt-2">{tier.description}</p>
                  {tier.highlight && (
                    <p className="text-sm text-blue-600 font-medium mt-1">{tier.highlight}</p>
                  )}
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link href="/payment-plans">
                    <Button 
                      className={`w-full h-12 ${tier.popular ? 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700' : ''}`}
                      variant={tier.popular ? "default" : "outline"}
                    >
                      {tier.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content Section - AI Course Information */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6 text-gray-900 dark:text-white">
              Comprehensive AI Education for Every Career Level
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto">
              Master artificial intelligence, machine learning, and deep learning with a practical curriculum built around guided stories, applied examples, coding practice, and production-minded projects.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Why Choose AI Academy Pro?</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">01</Badge>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Story-Based AI Curriculum</h4>
                    <p className="text-gray-600 dark:text-gray-300">75+ courses in active tracks covering AI foundations, Python, machine learning, neural networks, generative AI, and system design.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">02</Badge>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Hands-On Python Programming</h4>
                    <p className="text-gray-600 dark:text-gray-300">Learn AI development with Python, TensorFlow, PyTorch, and modern machine learning workflows used in practical projects.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">03</Badge>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Professional AI Certification</h4>
                    <p className="text-gray-600 dark:text-gray-300">Earn professional certificates in artificial intelligence and machine learning as you complete structured tracks.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">04</Badge>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Real-World AI Projects</h4>
                    <p className="text-gray-600 dark:text-gray-300">Build AI applications, create neural networks, and deploy machine learning models to production environments.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl">
              <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white">AI Course Curriculum Overview</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white">Foundation Track</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">AI basics, pattern recognition, detective methodology</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white">Programming & Development</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Python mastery, vector databases, RAG systems, AI agents</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white">Machine Learning Mastery</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Supervised/unsupervised learning, model evaluation, feature engineering</p>
                </div>
                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white">Deep Learning & Neural Networks</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">CNNs, RNNs, transformers, attention mechanisms</p>
                </div>
              </div>
            </div>
          </div>

          {/* AI Career Paths */}
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">AI Career Paths & Salary Potential</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Machine Learning Engineer</h4>
                <p className="text-2xl font-bold text-blue-600 mb-2">$120k - $180k</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">Design and implement ML algorithms, deploy AI models to production</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">AI Research Scientist</h4>
                <p className="text-2xl font-bold text-purple-600 mb-2">$140k - $220k</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">Develop cutting-edge AI technologies, publish research, innovate</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Data Scientist</h4>
                <p className="text-2xl font-bold text-green-600 mb-2">$95k - $150k</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">Extract insights from data, build predictive models, drive decisions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Career Development Hub */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              Career Development Hub
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Transform your technical skills into career success with AI-powered resume optimization, interview coaching, and career transition guidance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Resume Revamp Studio */}
            <Card className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-xl">Resume Revamp Studio</CardTitle>
                <p className="text-muted-foreground">
                  AI-powered resume analysis with ATS optimization and industry-specific templates
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Upload → Analyze → Revise workflow</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Voice coaching explanations</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Career switch transformation</span>
                  </div>
                </div>
                <Link href="/career/resume">
                  <Button className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:opacity-90">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Optimize Resume
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Career Pivot Explorer */}
            <Card className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-xl">Career Pivot Explorer</CardTitle>
                <p className="text-muted-foreground">
                  Discover transition paths with success probability analysis and real examples
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>3-5 personalized career paths</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Real success story examples</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Transferable skills mapping</span>
                  </div>
                </div>
                <Link href="/career/pivot">
                  <Button className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:opacity-90">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Explore Paths
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Mock Interview Coach */}
            <Card className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-xl">Mock Interview Coach</CardTitle>
                <p className="text-muted-foreground">
                  Practice interviews with AI feedback and STAR framework validation
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Real-time AI feedback</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Confidence scoring</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Industry-specific questions</span>
                  </div>
                </div>
                <Link href="/career/interview">
                  <Button className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:opacity-90">
                    <Brain className="w-4 h-4 mr-2" />
                    Start Practice
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link href="/career">
              <Button size="lg" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:opacity-90 px-8">
                <Trophy className="w-5 h-5 mr-2" />
                Access Full Career Hub
              </Button>
            </Link>
            <p className="text-sm text-muted-foreground mt-2">
              Complete career development toolkit for technical professionals
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Master AI and Transform Your Career?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of professionals advancing their careers with comprehensive artificial intelligence and machine learning education
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/payment-plans">
              <Button size="lg" className="bg-white text-blue-700 hover:bg-gray-100 px-8">
                <Sparkles className="w-4 h-4 mr-2" />
                Start AI Learning Journey
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8">
              <Eye className="w-4 h-4 mr-2" />
              Explore Free AI Track
            </Button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
